// 📁 src/components/VoiceInputUX.jsx
// 🎤 UX VOZ P0 - Sistema Push-to-Talk con feedback pedagógico integrado
// Especificación completa: Input transformable + Barras animadas + Pedagogía first-use

import React, { useState, useEffect, useRef } from 'react';
import { Mic, Square, Loader2, AlertCircle } from 'lucide-react';
import { useVoiceInput } from '../hooks/useVoiceInput';

/**
 * Sistema completo de input con voz integrado
 * 
 * ARQUITECTURA P0:
 * 1. Input text se convierte en "pantalla de estado" durante grabación
 * 2. Feedback visual obligatorio (barras animadas) dentro del input
 * 3. Pedagogía first-use (overlay flotante, 1 sola vez)
 * 4. Errores pedagógicos (toast, sin castigo)
 * 5. Botón circular sin texto
 * 
 * ESTADOS:
 * - idle: Input normal, mic gris neutro
 * - recording: Input bloqueado + borde rojo + barras animadas + mic rojo pulsante
 * - processing: Input bloqueado + loader
 * - error: Toast flotante, mic vuelve a idle en < 500ms
 * 
 * @param {string} inputValue - Valor del input de texto
 * @param {Function} onInputChange - (value) => void
 * @param {Function} onSend - () => void - Enviar mensaje de texto
 * @param {Function} onVoiceSend - (text) => void - Enviar mensaje de voz
 * @param {boolean} disabled - Deshabilitar todo el sistema
 * @param {string} placeholder - Placeholder del input (idle)
 * @param {boolean} isSending - Estado de envío (muestra loader en botón send)
 * @param {string} assistantAudioState - Estado de audio IA: 'idle' | 'playing' * @param {string} assistantState - Estado de procesamiento IA: 'idle' | 'thinking' | 'responding' * @param {Function} onStopAudio - Callback para detener audio IA
 * @param {Object} voiceOptions - Opciones para useVoiceInput
 * @param {Function} onAnalyticsEvent - Telemetría
 */
const VoiceInputUX = ({
  inputValue,
  onInputChange,
  onSend,
  onVoiceSend,
  disabled = false,
  placeholder = 'Escribe tu mensaje...',
  isSending = false,
  assistantAudioState = 'idle',
  assistantState = 'idle',
  onStopAudio = null,
  voiceOptions = {},
  onAnalyticsEvent = null
}) => {
  const inputRef = useRef(null);
  // 🎟️ TICKET-2: Overlays eliminados (simplifica UX)
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [shouldPulseMic, setShouldPulseMic] = useState(false);
  const lastAssistantStateRef = useRef(assistantState);

  const {
    isIdle,
    isPermissionPending,
    isReady,
    isRecording,
    isStopping,
    isProcessing,
    isError,
    errorMessage: voiceError,
    voiceState,
    audioSignalStrength,
    hasDetectedSpeech,
    requestPermission,
    startRecording,
    stopRecording,
    cancelRecording
  } = useVoiceInput(onVoiceSend, {
    ...voiceOptions,
    onAnalyticsEvent
  });

  // 🎟️ TICKET-2: Tooltip permission-granted eliminado (no necesario)
  // El modal aparece 1 sola vez, el estado del botón es claro

  // 🎟️ TICKET-2: Detección automática mobile/desktop
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      const mobile = /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent)
        || (window.innerWidth <= 768);
      setIsMobileDevice(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 🎟️ P1 UX PREMIUM: Pulse sutil al recibir turno (1 vez por turno)
  useEffect(() => {
    const wasThinkingOrResponding = 
      lastAssistantStateRef.current === 'thinking' || 
      lastAssistantStateRef.current === 'responding';
    
    const nowIdle = assistantState === 'idle';
    const isCleanTurn = 
      nowIdle && 
      assistantAudioState === 'idle' && 
      voiceState === 'idle' && 
      inputValue.length === 0;
    
    // Activar pulse solo en transición hacia turno limpio
    if (wasThinkingOrResponding && isCleanTurn) {
      setShouldPulseMic(true);
      console.log('🎯 [P1_VOICE_CTA] Activando pulse sutil: turno del usuario');
      
      // Desactivar después de 600ms (1 ciclo completo)
      const timer = setTimeout(() => {
        setShouldPulseMic(false);
      }, 600);
      
      return () => clearTimeout(timer);
    }
    
    // Actualizar referencia
    lastAssistantStateRef.current = assistantState;
  }, [assistantState, assistantAudioState, voiceState, inputValue]);

  // 🎟️ TICKET-2: First-use tutorial eliminado (simplifica UX)

  // Mostrar/ocultar toast cuando hay error
  useEffect(() => {
    if (isError && voiceError) {
      setErrorMessage(voiceError);
      setShowErrorToast(true);
      
      // Auto-dismiss después de 2.5s
      const timer = setTimeout(() => {
        setShowErrorToast(false);
      }, 2500);
      
      return () => clearTimeout(timer);
    } else if (!isError) {
      // 🔧 FIX: Ocultar toast cuando el estado ya no es ERROR
      setShowErrorToast(false);
    }
  }, [isError, voiceError]);

  // Auto-resize del textarea
  useEffect(() => {
    if (inputRef.current && !isRecording) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = inputRef.current.scrollHeight + 'px';
    }
  }, [inputValue, isRecording]);

  /**
   * 🎟️ TICKET-2/3: Pointer Events con patrón adaptativo mobile/desktop + FSM
   * MOBILE/TABLET: Tap para iniciar/detener (toggle) - UX clara sin mantener presionado
   * DESKTOP: Mantener presionado (hold) - patrón tradicional PTT
   */
  const handleMicPointerDown = async (e) => {
    e.preventDefault();
    
    // 🎯 P0/P1: Bloqueo durante reproducción de audio IA
    const canInteract = (isIdle || isReady || isRecording);
    if (disabled || !canInteract || assistantAudioState === 'playing' || assistantState === 'thinking') return;
    
    // Capturar pointer para recibir eventos incluso fuera del botón
    e.currentTarget.setPointerCapture(e.pointerId);
    console.log('[VoiceInputUX] Pointer capturado:', e.pointerId);
    
    // 🎟️ TICKET-2: First-use overlay eliminado (simplificación UX)
    // Marcar tutorial visto (sin mostrar overlay)
    const hasSeenTutorial = localStorage.getItem('voice_tutorial_seen');
    if (!hasSeenTutorial) {
      localStorage.setItem('voice_tutorial_seen', 'true');
    }
    
    // 📱 PATRÓN MOBILE/TABLET: TOGGLE (tap inicia, tap detiene)
    if (isMobileDevice) {
      if (isRecording) {
        // Ya está grabando → DETENER
        console.log('[VoiceInputUX][MOBILE] Tap para detener grabación');
        stopRecording();
      } else if (isIdle || isReady) {
        // No está grabando → INICIAR
        console.log('[VoiceInputUX][MOBILE] Tap para iniciar grabación');
        await startRecording();
      }
      return; // Mobile no usa pointerUp para detener
    }
    
    // 🖥 PATRÓN DESKTOP: HOLD (mantener presionado)
    if (isIdle || isReady) {
      console.log('[VoiceInputUX][DESKTOP] Hold para grabar');
      await startRecording();
    }
  };

  const handleMicPointerUp = (e) => {
    e.preventDefault();
    
    // 📱 MOBILE: Ignora pointerUp (usa toggle en pointerDown)
    if (isMobileDevice) {
      // Liberar captura sin detener grabación
      e.currentTarget.releasePointerCapture(e.pointerId);
      console.log('[VoiceInputUX][MOBILE] Pointer liberado (no detiene grabación)');
      return;
    }
    
    // 🖥 DESKTOP: Detiene al soltar (patrón PTT tradicional)
    if (isRecording) {
      e.currentTarget.releasePointerCapture(e.pointerId);
      console.log('[VoiceInputUX][DESKTOP] Pointer liberado - deteniendo grabación');
      stopRecording();
    }
  };

  const handleMicPointerCancel = (e) => {
    e.preventDefault();
    if (isRecording) {
      console.log('[VoiceInputUX] Pointer cancelado, cancelando grabación');
      cancelRecording();
    }
  };

  /**
   * 🎟️ TICKET-1: Bloquear menú contextual en tablets Samsung
   */
  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('[VoiceInputUX] Menú contextual bloqueado');
  };

  /**
   * 🎟️ TICKET-1: Salidas de emergencia (blur, visibilitychange)
   */
  useEffect(() => {
    const handleBlur = () => {
      if (isRecording) {
        console.log('[VoiceInputUX] Window blur - cancelando grabación');
        cancelRecording();
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden && isRecording) {
        console.log('[VoiceInputUX] Tab oculto - cancelando grabación');
        cancelRecording();
      }
    };

    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isRecording, cancelRecording]);

  /**
   * Handlers del input
   */
  const handleKeyPress = (e) => {
    // 🎯 P0/P1: Bloqueo durante reproducción de audio IA
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!disabled && inputValue.trim() && assistantAudioState !== 'playing' && assistantState !== 'thinking') {
        onSend();
      }
    }
  };

  // Cancelar con ESC durante grabación
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isRecording) {
        cancelRecording();
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isRecording, cancelRecording]);

  /**
   * Clases del input según estado
   * P0 DETERMINISTIC: Matriz de permisos estricta
   */
  const getInputClasses = () => {
    const base = `w-full px-4 py-2.5 md:px-5 md:py-4
      rounded-2xl md:rounded-3xl resize-none
      transition-all duration-200 ease-in-out
      text-base leading-normal md:leading-relaxed
      min-h-[44px] md:min-h-[64px]
      max-h-[120px] md:max-h-[200px]
      shadow-none bg-white outline-none ring-0 focus:ring-0 focus:outline-none`;

    // P0 VISUAL: Grabación = borde rojo fino, sin glow excesivo
    if (isRecording) {
      return `${base}
        border-2 border-red-500 bg-red-50/40
        text-gray-500 cursor-not-allowed`;
    }

    // P0 DETERMINISTIC: Processing = hard disabled (no editable)
    if (isProcessing) {
      return `${base}
        border border-gray-300 bg-gray-100
        text-gray-400 cursor-not-allowed opacity-75`;
    }

    // 🎯 P0/P1 + 🧠 ASSISTANTSTATE: Bloqueado durante reproducción o thinking
    if (isPermissionPending || disabled || assistantAudioState === 'playing' || assistantState === 'thinking') {
      return `${base}
        border border-gray-200 bg-gray-50
        text-gray-400 cursor-not-allowed opacity-75`;
    }

    return `${base}
      border border-gray-200 bg-white
      hover:border-gray-300
      `;
  };

  /**
   * Placeholder dinámico según estado
   * P0 VISUAL: Sin emojis - diseño ejecutivo sobrio
   * P0 DETERMINISTIC: Indicar estado actual claramente
   * P1 UX PREMIUM: Orientar a voz cuando es turno limpio del usuario
   * 📱 MOBILE: Tap toggle | 🖥 DESKTOP: Hold
   */
  const getPlaceholder = () => {
    if (isRecording) {
      return isMobileDevice 
        ? 'Grabando… toca de nuevo para enviar'
        : 'Grabando… suelta para enviar';
    }
    if (isProcessing) return 'Procesando...';
    if (assistantState === 'thinking') return 'Pensando…'; // 🧠 P0 ASSISTANTSTATE
    if (assistantAudioState === 'playing') return placeholder; // Ya viene desde parent
    
    // 🎯 P1 UX PREMIUM: Si es turno limpio del usuario + input vacío → orientar a voz
    const isCleanUserTurn = 
      assistantState === 'idle' && 
      assistantAudioState === 'idle' && 
      voiceState === 'idle' && 
      inputValue.length === 0;
    
    if (isCleanUserTurn) {
      return isMobileDevice 
        ? 'Toca el micrófono para hablar'
        : 'Mantén presionado para hablar';
    }
    
    return placeholder;
  };

  /**
   * Clases del botón de micrófono
   * P0 DETERMINISTIC: Estados claros y cursor apropiado
   */
  const getMicButtonClasses = () => {
    const base = `w-11 h-11 md:w-14 md:h-14 rounded-full flex items-center justify-center shrink-0
      transition-all duration-200 ease-out
      select-none touch-none
      focus:outline-none focus:ring-2 focus:ring-offset-2`;

    // P0 VISUAL: Rojo sólido flat con ring pulsante para indicar grabación activa
    if (isRecording) {
      return `${base}
        bg-red-500 text-white
        hover:bg-red-600
        shadow-md
        scale-105
        ring-4 ring-red-200 ring-offset-1`;  
    }

    // P0 DETERMINISTIC: Processing bloqueado
    if (isProcessing) {
      return `${base} bg-gray-200 text-gray-400 cursor-not-allowed opacity-50`;
    }

    if (isError) {
      return `${base}
        bg-red-50 text-red-600
        border border-red-200 hover:bg-red-100
        focus:ring-red-400`;
    }

    // 🎯 P0/P1 + 🧠 ASSISTANTSTATE: Mic deshabilitado durante audio IA o thinking
    if (assistantAudioState === 'playing' || assistantState === 'thinking') {
      return `${base} bg-gray-100 text-gray-400 cursor-not-allowed opacity-50`;
    }

    // 🎯 P1 UX PREMIUM: Mic como CTA primario cuando es turno limpio del usuario
    const isCleanUserTurn = 
      assistantState === 'idle' && 
      assistantAudioState === 'idle' && 
      voiceState === 'idle' && 
      inputValue.length === 0;
    
    if (isCleanUserTurn) {
      const pulseClass = shouldPulseMic ? 'animate-pulse-once' : '';
      return `${base}
        bg-gradient-to-br from-indigo-500 to-indigo-600 text-white
        hover:from-indigo-600 hover:to-indigo-700
        hover:shadow-lg hover:scale-105
        shadow-md
        focus:ring-indigo-400
        ${pulseClass}`;
    }

    return `${base}
      bg-gray-100 text-gray-600
      hover:bg-gray-200 active:scale-95
      border border-gray-200
      focus:ring-gray-400
      ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`;
  };

  return (
    <div className="relative w-full min-[768px]:sticky min-[768px]:bottom-0 min-[768px]:z-30 min-[768px]:bg-white min-[768px]:px-0 min-[768px]:py-0 min-[768px]:w-full min-[768px]:pb-[env(safe-area-inset-bottom)] pb-[env(safe-area-inset-bottom)]">
      {/* 🎟️ TICKET-2: Overlay first-use eliminado (simplifica UX) */}

      {/* Overlay de permisos (P0-VOICE-UX) */}
      {isPermissionPending && (
        <div className="absolute inset-0 z-50 flex items-center justify-center
          bg-white/95 backdrop-blur-sm rounded-2xl">
          <div className="text-center px-6 py-8">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Mic className="w-6 h-6 text-indigo-600" />
              <svg className="w-5 h-5 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <p className="text-sm text-gray-700 font-medium mb-1">
              Permite el acceso al micrófono
            </p>
            <p className="text-xs text-gray-500">
              {isMobileDevice ? 'Toca para hablar, toca de nuevo para enviar' : 'Mantén presionado para hablar'}
            </p>
          </div>
        </div>
      )}

      {/* 🎟️ TICKET-2: Overlays first-use y permission-granted eliminados (simplifica UX) */}

      {/* Toast de error */}
      {showErrorToast && (
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 z-40
          bg-red-50 border-2 border-red-200 px-4 py-3 rounded-xl shadow-xl
          animate-fade-in max-w-xs">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">
              <p className="font-medium mb-1">No se detectó voz.</p>
              <p className="text-xs text-red-600">Mantén presionado mientras hablas.</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center flex-nowrap gap-3 min-h-[44px] md:min-h-[56px] w-full">
        {/* Input container con overlay de barras */}
        <div className="flex-1 min-w-0 relative rounded-2xl border border-gray-200 bg-white shadow-none flex items-center min-h-[44px] md:min-h-[56px]">
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => {
              if (isRecording || isProcessing || assistantAudioState === 'playing' || assistantState === 'thinking') return;
              onInputChange(e.target.value);
              // AUTOSIZE: reset height, set to min(scrollHeight, maxHeight)
              const textarea = e.target;
              textarea.style.height = 'auto';
              const lineHeight = 20;
              const minRows = 1;
              const maxRows = 4;
              const padding = 0;
              const maxHeight = lineHeight * maxRows + padding;
              textarea.style.height = Math.min(textarea.scrollHeight, maxHeight) + 'px';
              textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
            }}
            onKeyPress={handleKeyPress}
            placeholder={assistantAudioState === 'playing' ? 'Reproduciendo respuesta…' : getPlaceholder()}
            disabled={disabled || isRecording || isProcessing || assistantAudioState === 'playing' || assistantState === 'thinking'}
            readOnly={assistantAudioState === 'playing'}
            rows={1}
            className="w-full bg-transparent shadow-none outline-none ring-0 focus:ring-0 focus:outline-none py-3 md:py-4 px-4 text-base leading-normal resize-none whitespace-pre-wrap word-break break-word flex items-center"
            style={{
              minHeight: '44px',
              maxHeight: '80px',
              overflowY: inputValue.length > 100 ? 'auto' : 'hidden',
              lineHeight: '20px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              display: 'flex',
              alignItems: 'center',
              paddingTop: '12px',
              paddingBottom: '12px'
            }}
          />
          {/* Barras animadas - P0 VISUAL: Minimalistas flat (estilo Stripe/Linear) */}
          {isRecording && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="flex items-center gap-1.5">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="w-1 bg-red-500 animate-wave"
                    style={{
                      height: '14px',
                      animationDelay: `${i * 0.15}s`,
                      animationDuration: '1s'
                    }}
                  />
                ))}
              </div>
            </div>
          )}
          {isProcessing && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
            </div>
          )}
        </div>
        {/* Rail de acciones */}
        <div className="flex flex-none gap-3 min-w-[48px]">
          {/* Playback activo: solo STOP visible */}
          {assistantAudioState === 'playing' && onStopAudio && (
            <button
              onClick={onStopAudio}
              className="w-11 h-11 md:w-14 md:h-14 rounded-full flex items-center justify-center shrink-0 bg-gradient-to-br from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700 transition-all duration-200 ease-out active:scale-95 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 animate-pulse"
              title="Detener audio"
              type="button"
            >
              <svg 
                className="w-5 h-5 md:w-6 md:h-6" 
                fill="currentColor" 
                viewBox="0 0 24 24"
              >
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
            </button>
          )}
          {/* Playback inactivo: MIC y SEND visibles */}
          {assistantAudioState !== 'playing' && <>
            {/* 🎟️ TICKET-1: Botón micrófono con Pointer Events + hint label */}
            <div className="flex flex-col items-center gap-0.5">
              <button
                onPointerDown={handleMicPointerDown}
                onPointerUp={handleMicPointerUp}
                onPointerCancel={handleMicPointerCancel}
                onContextMenu={handleContextMenu}
                disabled={disabled || isProcessing || assistantAudioState === 'playing' || assistantState === 'thinking'}
                className={getMicButtonClasses()}
                title={
                  assistantAudioState === 'playing' 
                    ? 'Detén el audio para hablar'
                    : assistantState === 'thinking'
                    ? 'Esperando respuesta de la IA...'
                    : isProcessing
                    ? 'Procesando audio...'
                    : isRecording 
                    ? (isMobileDevice ? 'Toca de nuevo para enviar' : 'Suelta para enviar')
                    : (isMobileDevice ? 'Toca para hablar' : 'Mantén presionado para hablar')
                }
                type="button"
                style={{
                  touchAction: 'none',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  WebkitTouchCallout: 'none'
                }}
              >
                {isRecording ? (
                  <Square className="w-5 h-5" />
                ) : isProcessing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : isError ? (
                  <AlertCircle className="w-5 h-5" />
                ) : (
                  <Mic className="w-5 h-5" />
                )}
              </button>
              {/* Hint label contextual bajo el botón */}
              {!isProcessing && assistantAudioState !== 'playing' && assistantState !== 'thinking' && (
                <span
                  className={`text-[9px] font-semibold tracking-wide select-none leading-none transition-colors duration-200 ${
                    isRecording
                      ? 'text-red-400 animate-pulse'
                      : 'text-slate-400'
                  }`}
                >
                  {isRecording
                    ? (isMobileDevice ? 'toca · enviar' : 'suelta')
                    : (isMobileDevice ? 'toca' : 'mantén')}
                </span>
              )}
            </div>
            <button
              onClick={onSend}
              disabled={!inputValue.trim() || isSending || disabled || isRecording || assistantAudioState === 'playing' || assistantState === 'thinking'}
              className={`w-11 h-11 md:w-14 md:h-14 rounded-full flex items-center justify-center shrink-0 transition-all duration-200 ease-out active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2
                ${
                  inputValue.trim() 
                    ? 'bg-gradient-to-br from-indigo-500 to-indigo-600 text-white hover:from-indigo-600 hover:to-indigo-700 focus:ring-indigo-400'
                    : 'bg-gray-200 text-gray-500 hover:bg-gray-300 focus:ring-gray-300'
                }
                disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed disabled:hover:scale-100
              `}
              title={
                !inputValue.trim() 
                  ? 'Escribe un mensaje' 
                  : assistantState === 'thinking'
                  ? 'Esperando respuesta de la IA...'
                  : assistantAudioState === 'playing' 
                  ? 'Espera a que termine el audio' 
                  : 'Enviar mensaje'
              }
            >
              {isSending ? (
                <Loader2 className="w-5 h-5 md:w-6 md:h-6 animate-spin" />
              ) : (
                <svg 
                  className="w-5 h-5 md:w-6 md:h-6" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" 
                  />
                </svg>
              )}
            </button>
          </>}
        </div>
      </div>

      {/* Estilos para animación de barras */}
      <style>{`
        @keyframes wave {
          0%, 100% { height: 16px; }
          50% { height: 28px; }
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-in-down {
          from { opacity: 0; transform: translate(-50%, -10px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
        .animate-wave {
          animation: wave 0.8s ease-in-out infinite;
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        .animate-fade-in-down {
          animation: fade-in-down 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default VoiceInputUX;
